/**
 * What a filing packet includes ahead of the exhibits, and whether the
 * exhibits themselves go in. The firm sets the defaults in Case Preparation;
 * the Assemble card shows this case's work products and lets the attorney
 * turn each one off before the one generate_packet call.
 */
import type { DraftType } from "@/api/types/draft";
import { isPrivileged, type FsNode } from "../../caseFiles/types";
import type { WorkspaceItem } from "../workspace/workspaceModel";

export const PACKET_CONTENT_KINDS = [
  "cover_letter",
  "forms",
  "letters",
  "exhibit_list",
  "exhibits",
  "other"
] as const;

export type PacketContentKind = (typeof PACKET_CONTENT_KINDS)[number];

export type PacketContents = Record<PacketContentKind, boolean>;

export const DEFAULT_PACKET_CONTENTS: PacketContents = {
  cover_letter: true,
  forms: true,
  letters: true,
  exhibit_list: true,
  exhibits: true,
  other: false
};

export const PACKET_CONTENT_LABELS: Record<PacketContentKind, string> = {
  cover_letter: "Cover Letter",
  forms: "Filled Forms",
  letters: "Letters",
  exhibit_list: "Exhibit List",
  exhibits: "Exhibits",
  other: "Other Work Products"
};

const LETTER_TYPES = new Set<string>([
  "legal_brief",
  "support_letter",
  "rfe_response",
  "noir_noid_response"
]);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** The firm's saved contents, CaseBlink's include where a key is missing. */
export const packetContentsOf = (raw: unknown): PacketContents => {
  const src = isRecord(raw) && isRecord(raw.contents) ? raw.contents : raw;
  const row = isRecord(src) ? src : {};
  const bool = (key: PacketContentKind) =>
    typeof row[key] === "boolean" ? row[key] : DEFAULT_PACKET_CONTENTS[key];
  return {
    cover_letter: bool("cover_letter"),
    forms: bool("forms"),
    letters: bool("letters"),
    exhibit_list: bool("exhibit_list"),
    exhibits: bool("exhibits"),
    other: bool("other")
  };
};

export const samePacketContents = (a: PacketContents, b: PacketContents) =>
  PACKET_CONTENT_KINDS.every((k) => a[k] === b[k]);

/** CaseBlink's packet order. The exhibits always print after the work products. */
export const DEFAULT_PACKET_ORDER: readonly PacketContentKind[] = [
  "cover_letter",
  "forms",
  "letters",
  "other",
  "exhibit_list",
  "exhibits"
];

const isKind = (v: unknown): v is PacketContentKind =>
  typeof v === "string" &&
  (PACKET_CONTENT_KINDS as readonly string[]).includes(v);

/**
 * The firm's saved order (`contents_order`): each kind once, kinds it left
 * out in CaseBlink's order, the exhibits last.
 */
export const packetOrderOf = (raw: unknown): PacketContentKind[] => {
  const saved =
    isRecord(raw) && Array.isArray(raw.contents_order)
      ? raw.contents_order
      : [];
  const out: PacketContentKind[] = [];
  for (const kind of [...saved, ...DEFAULT_PACKET_ORDER])
    if (isKind(kind) && kind !== "exhibits" && !out.includes(kind))
      out.push(kind);
  return [...out, "exhibits"];
};

export const samePacketOrder = (
  a: readonly PacketContentKind[],
  b: readonly PacketContentKind[]
) => a.length === b.length && a.every((k, i) => k === b[i]);

/** What `default_packet_settings` stores for the order; omit CaseBlink's. */
export const storedPacketOrder = (
  order: readonly PacketContentKind[]
): PacketContentKind[] | undefined => {
  const full = packetOrderOf({ contents_order: order });
  return samePacketOrder(full, DEFAULT_PACKET_ORDER) ? undefined : full;
};

/** Which kind a draft is for the packet, or null (research memos stay out). */
export const kindOfDraft = (draft: {
  draft_type: DraftType | string;
  visa_subtype?: string | null;
}): PacketContentKind | null => {
  if (
    String(draft.draft_type) === "custom" &&
    draft.visa_subtype === "research_memo"
  )
    return null;
  const type = String(draft.draft_type);
  if (type === "cover_letter") return "cover_letter";
  if (type === "exhibit_list") return "exhibit_list";
  if (LETTER_TYPES.has(type)) return "letters";
  return "other";
};

export const kindOfItem = (item: WorkspaceItem): PacketContentKind | null => {
  if (item.artifact.kind === "form") return "forms";
  if (item.artifact.kind !== "draft") return null;
  return kindOfDraft({ draft_type: item.draftType ?? "" });
};

export type PacketPart = {
  /** Draft id, `form:<id>`, or `exhibits`. */
  key: string;
  /** What generate_packet takes: a draft id or `form:<id>`. Null for exhibits. */
  frontId: string | null;
  title: string;
  kind: PacketContentKind;
  /** Ready to print; a filling form or idle draft is listed but called out.
   *  The exhibit list always is: the build writes one that has no document. */
  ready: boolean;
};

export const partOfItem = (item: WorkspaceItem): PacketPart | null => {
  const kind = kindOfItem(item);
  if (!kind || kind === "exhibits") return null;
  const form = item.artifact.kind === "form";
  return {
    key: form ? `form:${item.artifact.id}` : item.artifact.id,
    frontId: form ? `form:${item.artifact.id}` : item.artifact.id,
    title: item.artifact.title,
    kind,
    ready:
      kind === "exhibit_list" ||
      item.status === "ready" ||
      item.status === "stale" ||
      item.fill?.state === "filled"
  };
};

export const includedExhibits = (nodes: FsNode[]): FsNode[] =>
  nodes.filter(
    (n) =>
      n.kind === "file" &&
      !n.is_deleted &&
      n.packet_inclusion !== "excluded" &&
      !isPrivileged(n)
  );

export const exhibitsPart = (nodes: FsNode[]): PacketPart => {
  const files = includedExhibits(nodes);
  return {
    key: "exhibits",
    frontId: null,
    title:
      files.length === 1
        ? "1 included exhibit"
        : `${files.length} included exhibits`,
    kind: "exhibits",
    ready: files.length > 0
  };
};

/**
 * A new packet's parts: the kinds the firm includes, in its order, then the
 * kinds it leaves unchecked, in its order, then the exhibits.
 */
export const packetPartsOf = (
  drafts: WorkspaceItem[],
  forms: WorkspaceItem[],
  lists: WorkspaceItem[],
  nodes: FsNode[],
  order: readonly PacketContentKind[] = DEFAULT_PACKET_ORDER,
  contents: PacketContents = DEFAULT_PACKET_CONTENTS
): PacketPart[] => {
  const full = packetOrderOf({ contents_order: order });
  const rank = (kind: PacketContentKind) =>
    kind === "exhibits"
      ? 2 * full.length
      : full.indexOf(kind) + (contents[kind] ? 0 : full.length);
  const parts = [...drafts, ...forms, ...lists].flatMap((item) => {
    const part = partOfItem(item);
    return part ? [part] : [];
  });
  const seen = new Set<string>();
  const unique = parts.filter((p) => {
    if (seen.has(p.key)) return false;
    seen.add(p.key);
    return true;
  });
  return [...unique, exhibitsPart(nodes)].sort(
    (a, b) => rank(a.kind) - rank(b.kind)
  );
};

/** The attorney's drag order, then any part they haven't placed yet. */
export const applyPacketOrder = (
  parts: PacketPart[],
  order: string[]
): PacketPart[] => {
  if (!order.length) return parts;
  const byKey = new Map(parts.map((p) => [p.key, p]));
  const seen = new Set<string>();
  const out: PacketPart[] = [];
  for (const key of order) {
    const part = byKey.get(key);
    if (part) {
      out.push(part);
      seen.add(key);
    }
  }
  for (const part of parts) if (!seen.has(part.key)) out.push(part);
  return out;
};

export const movePacketPart = (
  order: string[],
  fromKey: string,
  toKey: string
): string[] => {
  const next = [...order];
  const from = next.indexOf(fromKey);
  const to = next.indexOf(toKey);
  if (from < 0 || to < 0 || from === to) return order;
  next.splice(from, 1);
  next.splice(to, 0, fromKey);
  return next;
};

/** Whether this part is in the next build: a hand pick, else the firm default. */
export const partOn = (
  part: PacketPart,
  picks: Record<string, boolean>,
  defaults: PacketContents
): boolean => picks[part.key] ?? defaults[part.kind];

export const selectedParts = (
  parts: PacketPart[],
  picks: Record<string, boolean>,
  defaults: PacketContents
): PacketPart[] => parts.filter((p) => partOn(p, picks, defaults));

/** Cover letters lead only when the first checked work product is one. */
export const coverLetterFirstOf = (
  parts: PacketPart[],
  picks: Record<string, boolean>,
  defaults: PacketContents
): boolean => {
  const first = selectedParts(parts, picks, defaults).find((p) => p.frontId);
  return !first || first.kind === "cover_letter";
};

export const selectedFrontMatter = (
  parts: PacketPart[],
  picks: Record<string, boolean>,
  defaults: PacketContents
): string[] =>
  selectedParts(parts, picks, defaults).flatMap((p) =>
    p.frontId ? [p.frontId] : []
  );

export const includeEvidence = (
  picks: Record<string, boolean>,
  defaults: PacketContents
): boolean => picks.exhibits ?? defaults.exhibits;

/** What `default_packet_settings` stores for contents; omit the all-default set. */
export const storedPacketContents = (
  contents: PacketContents
): PacketContents | undefined =>
  samePacketContents(contents, DEFAULT_PACKET_CONTENTS) ? undefined : contents;

export const packetArgsOf = (
  parts: PacketPart[],
  picks: Record<string, boolean>,
  defaults: PacketContents
): PacketPromptArgs => ({
  frontMatter: selectedFrontMatter(parts, picks, defaults),
  includeEvidence: includeEvidence(picks, defaults),
  coverLetterFirst: coverLetterFirstOf(parts, picks, defaults)
});

export type PacketPromptArgs = {
  frontMatter: string[];
  includeEvidence: boolean;
  coverLetterFirst: boolean;
};

export const EMPTY_PACKET_ARGS: PacketPromptArgs = {
  frontMatter: [],
  includeEvidence: true,
  coverLetterFirst: true
};
